import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Table from "./Table";
import Summary from "./Summary";
import Layout from "../../components/layout/index";
import './league.css'
import CategorySelector from "./CategorySelector";
import ArchiveContent from "./ArchiveContent"; // Import the archive component
import YearSelector from "../../components/league components/YearSelector"; // Import directly for Archive tab
import headerimg from "../../assets/assets/leage_table.png";
import ArchiveYearSelector from "../../components/league components/ArchiveYearSelector";

const League = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  
  // Initialize state from URL params with shorter parameter names
  const [category, setCategory] = useState(() => queryParams.get("c") || "t");
  const [subCategory, setSubCategory] = useState(() => {
    const sc = queryParams.get("sc");
    return sc === "pe" ? "pe" : sc === "m&a" ? "m&a" : "both";
  });
  const fromDate = "2013-01-01"
  const toDate = '2026-09-30';
  const [isChecked, setIsChecked] = useState(() => queryParams.get("oa") === "true");
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSearchForm, setShowSearchForm] = useState(false);
  const [sortedData, setSortedData] = useState(tableData);
  const [activeTab, setActiveTab] = useState(() => queryParams.get("tab") || "live");
  
  // Separate state for archive tab
  const currentYear = new Date().getFullYear() - 1;
  const [archiveYear, setArchiveYear] = useState(currentYear );
  const [archiveYearType, setArchiveYearType] = useState("cy");
  const [archiveLoading, setArchiveLoading] = useState(false);

  // Update URL with current state
  const updateUrlParams = (params) => {
    const newParams = new URLSearchParams(location.search);
    
    Object.keys(params).forEach((key) => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        newParams.set(key, params[key]);
      } else {
        newParams.delete(key);
      }
    });
  
    // Replace URL without reloading
    window.history.replaceState(
      null, 
      '', 
      `${location.pathname}?${newParams.toString()}`
    );
  };

  const toggleMainTableHandler = (mainTable) => {
    setCategory(mainTable);
    // Update URL immediately when category changes
    updateUrlParams({ c: mainTable });
  };
  
  const toggleSubTableHandler = (subTable) => {
    setSubCategory(subTable);
    // Update URL immediately when subcategory changes
    updateUrlParams({ sc: subTable });
  };

  useEffect(() => {
    setSortedData(tableData);
  }, [tableData]);

  // Sync state with URL parameters when URL changes
  useEffect(() => {
    const newQueryParams = new URLSearchParams(location.search);
    const urlCategory = newQueryParams.get("c");
    const urlSubCategory = newQueryParams.get("sc");
    const urlTab = newQueryParams.get("tab");
    const urlOtherAdvisory = newQueryParams.get("oa");

    if (urlCategory && (urlCategory === "t" || urlCategory === "l")) {
      setCategory(urlCategory);
    }
    if (urlSubCategory && (urlSubCategory === "pe" || urlSubCategory === "m&a")) {
      setSubCategory(urlSubCategory);
    } else if (urlSubCategory === null || urlSubCategory === undefined) {
      setSubCategory("both");
    }
    if (urlTab && (urlTab === "live" || urlTab === "archive")) {
      setActiveTab(urlTab);
    }
    if (urlOtherAdvisory !== null) {
      setIsChecked(urlOtherAdvisory === "true");
    }
  }, [location.search]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Update URL immediately when tab changes
    updateUrlParams({ tab: tab });
  };

  // Handler specifically for archive year changes
  const handleArchiveYearChange = (year, type) => {
    setArchiveYear(year);
    setArchiveYearType(type || "cy");
    setArchiveLoading(true);
    // Reset loading after a short delay to show visual feedback
    setTimeout(() => setArchiveLoading(false), 500);
  };

  return (
    <Layout>
      <div 
        className="relative bg-cover bg-center h-[30vh] md:h-[40vh] lg:h-[45vh]"
        style={{
          backgroundImage: ` url(${headerimg})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover'
        }}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <h2 className="text-3xl md:text-5xl lg:text-6xl pb-14 font-bold text-white tracking-wider">
            LEAGUE TABLES
          </h2>
        </div>
      </div>

      <div className="container mx-auto px-4 -mt-[5rem] relative z-10">
        <div className="bg-white rounded-lg shadow-lg p-6 lg:mx-32 md:mx-2">
          <Summary fromDate={fromDate} toDate={toDate} />
        </div>
      </div>

      <div className="container mx-auto px-4 my-8">
        <div className="lg:mx-32 md:mx-2">
          <div className="flex justify-center border-b border-gray-300">
            <div className="pb-2 relative cursor-pointer px-6 text-center mx-4" onClick={() => handleTabChange("live")}>
              <span className={`text-lg font-medium ${activeTab === "live" ? "text-[#bc8b20]" : "text-gray-600"}`}>Live</span>
              {activeTab === "live" && (
                <div className="absolute bottom-0 left-0 w-full h-1 bg-[#bc8b20]"></div>
              )}
            </div>
            <div className="pb-2 relative cursor-pointer px-6 text-center mx-4" onClick={() => handleTabChange("archive")}>
              <span className={`text-lg font-medium ${activeTab === "archive" ? "text-[#bc8b20]" : "text-gray-600"}`}>Archive</span>
              {activeTab === "archive" && (
                <div className="absolute bottom-0 left-0 w-full h-1 bg-[#bc8b20]"></div>
              )}
            </div>
          </div>
          
          {/* Content based on active tab */}
          <div className="mt-4">
            {activeTab === "live" ? (
              <div className="font-sans text-black">
                <div className="lg:mx-0 md:mx-0">
                  <CategorySelector 
                    category={category}
                    setCategory={setCategory}
                    toggleMainTableHandler={toggleMainTableHandler}
                    toggleSubTableHandler={toggleSubTableHandler}
                    subCategory={subCategory}
                    setSubCategory={setSubCategory}
                    fromDate={fromDate}
                    toDate={toDate}
                    isChecked={isChecked}
                    setIsChecked={setIsChecked}
                    setTableData={setTableData}
                    setLoading={setLoading}
                    showSearchForm={showSearchForm}
                    setShowSearchForm={setShowSearchForm}
                    sortedData={sortedData}
                  />
                </div>
                
                <div className="mt-6">
                  <Table 
                    loading={loading} 
                    subCategory={subCategory} 
                    sortedData={sortedData} 
                    setSortedData={setSortedData}  
                    tableData={tableData} 
                    setShowSearchForm={setShowSearchForm} 
                    showSearchForm={showSearchForm} 
                  />
                </div>
              </div>
            ) : (
              <div className="font-sans text-black">
                {/* Simplified archive controls - directly use YearSelector */}
                <div className="bg-white shadow-sm rounded-md p-4 mb-6">
                  <div className="flex justify-center items-center w-full">
                    <ArchiveYearSelector 
                      onYearChange={handleArchiveYearChange}
                    />
                  </div>
                </div>
                
                <div className="mt-6">
                  <ArchiveContent 
                    yearType={archiveYearType} 
                    selectedYear={archiveYear}
                    loading={archiveLoading}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default League;